import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import AppLayout from '../components/layout/AppLayout';
import DownloadFilesModal from '../components/ui/DownloadFilesModal';
import Icon from '../components/ui/Icon';
import Avatar from '../components/ui/Avatar';
import Chip from '../components/ui/Chip';
import Btn from '../components/ui/Btn';
import TimelineRow from '../components/ui/TimelineRow';

// ─── Helpers ─────────────────────────────────────────────
const buildStages = (doc) => {
  const done      = doc?.status === 'completed';
  const cancelled = doc?.status === 'cancelled';
  const inProg    = doc?.status === 'in_progress';
  const pct       = done ? 100 : (!doc?.recipient_count ? 0 : Math.round(((doc.signed_count || 0) / doc.recipient_count) * 100));

  return [
    { label: 'Sent',      meta: doc?.created_at_formatted || '—',  state: doc ? 'done' : 'pending' },
    { label: 'Viewed',    meta: '',                                  state: (inProg || done || pct > 0) ? 'done' : 'pending' },
    { label: 'Signing',   meta: pct > 0 && !done ? `${doc.signed_count}/${doc.recipient_count}` : '',
      state: cancelled ? 'blocked' : done ? 'done' : inProg ? 'active' : 'pending' },
    { label: 'Completed', meta: doc?.completed_at_formatted || '',  state: done ? 'done' : 'pending' },
  ];
};

const statusTone = (s) => {
  if (s === 'signed' || s === 'completed') return 'leaf';
  if (s === 'viewed' || s === 'in_progress') return 'navy';
  if (s === 'declined' || s === 'cancelled') return 'flame';
  return 'amber';
};
const statusIcon = (s) => {
  if (s === 'signed' || s === 'completed') return 'check';
  if (s === 'viewed' || s === 'in_progress') return 'eye';
  if (s === 'declined' || s === 'cancelled') return 'ban';
  return 'clock';
};
const statusLabel = (s) => {
  if (s === 'signed')     return 'Signed';
  if (s === 'completed')  return 'Completed';
  if (s === 'viewed')     return 'Viewed';
  if (s === 'in_progress')return 'Signing';
  if (s === 'declined')   return 'Declined';
  if (s === 'cancelled')  return 'Cancelled';
  return 'Waiting';
};

// ─── Main Component ──────────────────────────────────────
const DocumentDetailPage = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [doc,          setDoc]          = useState(null);
  const [bundle,       setBundle]       = useState(null); // all docs in same bundle
  const [activities,   setActivities]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [showDownload, setShowDownload] = useState(false);
  const [voiding,      setVoiding]      = useState(false);

  useEffect(() => { loadData(); }, [id]); // eslint-disable-line

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [docRes, actRes] = await Promise.all([
        api.getDocument(id),
        api.getDocumentActivities(id).catch(() => ({ activities: [] })),
      ]);
      setDoc(docRes.document || docRes);
      setActivities(actRes.activities || []);

      // Load bundle siblings
      const primary = docRes.document || docRes;
      if (primary.bundle_id) {
        const allRes = await api.getDocuments(1, 200);
        const siblings = (allRes.documents || []).filter(d => d.bundle_id === primary.bundle_id);
        setBundle(siblings.sort((a, b) => (a.bundle_order || 0) - (b.bundle_order || 0)));
      }
    } catch (err) {
      setError(err.message || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const handleVoid = async () => {
    if (!window.confirm('Void this envelope? This cannot be undone.')) return;
    setVoiding(true);
    try {
      const docsToVoid = bundle || (doc ? [doc] : []);
      for (const d of docsToVoid) await api.cancelDocument(d.id);
      await loadData();
    } catch (err) {
      alert('Failed to void: ' + err.message);
    } finally {
      setVoiding(false);
    }
  };

  // Loading skeleton
  if (loading) return (
    <AppLayout>
      <main className="flex-1 px-8 py-6">
        <div className="max-w-[1440px] mx-auto space-y-4">
          <div className="h-6 w-32 bg-ink-50 rounded animate-pulse" />
          <div className="h-10 w-96 bg-ink-50 rounded animate-pulse" />
          <div className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 p-6">
            <div className="h-20 bg-ink-50 rounded-xl animate-pulse" />
          </div>
          <div className="grid grid-cols-2 gap-5">
            {[1,2].map(i => (
              <div key={i} className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 p-6">
                <div className="space-y-3">
                  {[1,2,3].map(j => <div key={j} className="h-14 bg-ink-50 rounded-xl animate-pulse" />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </AppLayout>
  );

  if (error) return (
    <AppLayout>
      <main className="flex-1 px-8 py-6">
        <div className="max-w-[1440px] mx-auto">
          <button onClick={() => navigate('/dashboard')} className="text-[12px] text-ink-400 hover:text-ink-700 inline-flex items-center gap-1 mb-6">
            <Icon name="arrowLeft" size={12} /> All envelopes
          </button>
          <div className="bg-flame-50 ring-1 ring-flame-100 rounded-2xl p-8 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-white ring-1 ring-flame-100 flex items-center justify-center text-flame-600 flex-shrink-0">
              <Icon name="alert" size={18} />
            </div>
            <div>
              <div className="font-display text-[18px] font-semibold text-ink-900 mb-1">Could not load envelope</div>
              <div className="text-[13px] text-ink-600">{error}</div>
              <button onClick={loadData} className="mt-3 text-[13px] text-leaf-700 font-medium hover:text-leaf-800">
                Try again →
              </button>
            </div>
          </div>
        </div>
      </main>
    </AppLayout>
  );

  if (!doc) return null;

  const recipients    = doc.recipients || [];
  const signingDocs   = bundle || [doc];
  const stages        = buildStages(doc);
  const pct           = doc.status === 'completed' ? 100 : (!doc.recipient_count ? 0 : Math.round(((doc.signed_count || 0) / doc.recipient_count) * 100));
  const isGroup       = (doc.recipient_count || 0) > 1;
  const canVoid       = doc.status !== 'completed' && doc.status !== 'cancelled';

  // Build download documents list
  const dlDocs = signingDocs.map(d => ({ id: d.id, title: d.title, original_filename: d.original_filename }));

  // Activity icon tone mapping
  const actTone = (type) => {
    if (/sign|complet/i.test(type)) return { bg: 'bg-leaf-500', fg: 'text-white', icon: 'check' };
    if (/view|open/i.test(type))    return { bg: 'bg-ink-700',  fg: 'text-white', icon: 'eye' };
    if (/send|creat/i.test(type))   return { bg: 'bg-ink-200',  fg: 'text-ink-700', icon: 'mail' };
    if (/remind/i.test(type))       return { bg: 'bg-amber-100', fg: 'text-amber-600', icon: 'clock' };
    if (/void|cancel|declin/i.test(type)) return { bg: 'bg-flame-100', fg: 'text-flame-600', icon: 'ban' };
    return { bg: 'bg-paper-200', fg: 'text-ink-500', icon: 'infoI' };
  };

  return (
    <AppLayout fullHeight activeNav="signing">
      <main className="flex-1 overflow-auto thin-scroll px-6 lg:px-8 py-6">
        <div className="max-w-[1440px] mx-auto flex flex-col gap-4 min-h-0">

          {/* ── Back + header ────────────────────────────── */}
          <div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-[12px] text-ink-400 hover:text-ink-700 inline-flex items-center gap-1 mb-3 transition-colors"
            >
              <Icon name="arrowLeft" size={12} /> All envelopes
            </button>

            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Chip tone={isGroup ? 'navy' : 'ghost'} icon={isGroup ? 'users' : 'user'}>
                    {isGroup ? 'Group signing' : 'Self signing'}
                  </Chip>
                  <span className="text-[11px] font-mono tnum text-ink-400">#{String(id).slice(0, 12)}</span>
                </div>
                <h1 className="font-display text-[24px] sm:text-[28px] font-bold text-ink-900 leading-tight">
                  {doc.title || 'Untitled document'}
                </h1>
                <p className="text-[12.5px] text-ink-500 mt-1">
                  Created {doc.created_at_formatted || '—'} · {signingDocs.length} {signingDocs.length === 1 ? 'document' : 'documents'} · {doc.total_pages || '—'} pages
                  {doc.expires_at_formatted && ` · Expires ${doc.expires_at_formatted}`}
                </p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                <Btn tone="outline" icon="mail" size="md">Resend</Btn>
                {doc.status === 'completed' && (
                  <Btn tone="outline" icon="download" size="md" onClick={() => setShowDownload(true)}>
                    Download
                  </Btn>
                )}
                <Btn tone="outline" icon="refresh" size="md">Duplicate</Btn>
                {canVoid && (
                  <Btn tone="danger" icon="ban" size="md" onClick={handleVoid} disabled={voiding}>
                    {voiding ? 'Voiding…' : 'Void'}
                  </Btn>
                )}
              </div>
            </div>
          </div>

          {/* ── Big timeline strip ───────────────────────── */}
          <div className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 shadow-soft p-5 sm:p-6">
            <div className="flex items-start sm:items-center justify-between gap-4 mb-4 flex-wrap">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-leaf-700 mb-1">
                  {doc.status === 'completed' ? 'Completed' : doc.status === 'cancelled' ? 'Cancelled' : 'In progress'}
                </div>
                <div className="font-display text-[18px] font-semibold text-ink-900">
                  {doc.status === 'completed'
                    ? 'All signatures collected — envelope complete.'
                    : doc.status === 'cancelled'
                      ? 'This envelope has been voided.'
                      : `Awaiting signature${recipients.filter(r => r.status === 'pending').length > 1 ? 's' : ''}`}
                </div>
              </div>
              <div className="text-[12px] text-ink-500 text-right">
                <span className="tnum font-semibold text-ink-900 text-[15px]">{pct}%</span> complete
                {doc.recipient_count > 0 && (
                  <div className="text-[11px]">{doc.signed_count || 0} of {doc.recipient_count} recipients signed</div>
                )}
              </div>
            </div>
            <TimelineRow stages={stages} />
          </div>

          {/* ── Two-column body ──────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-4">

            {/* Left — Recipients ────────────────────────── */}
            <div className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 shadow-soft flex flex-col min-h-0">
              <div className="px-5 py-4 border-b border-ink-100 flex items-center justify-between">
                <div>
                  <div className="font-display text-[16px] font-semibold text-ink-900">Recipients</div>
                  <div className="text-[11.5px] text-ink-400">
                    {recipients.length} {recipients.length === 1 ? 'person' : 'people'}
                    {recipients.length > 0 && ` · ${recipients.filter(r => r.status === 'signed' || r.status === 'completed').length} signed`}
                  </div>
                </div>
              </div>

              <div className="overflow-auto thin-scroll divide-y divide-ink-50">
                {recipients.length === 0 ? (
                  <div className="px-5 py-8 text-center text-[13px] text-ink-400">No recipients yet.</div>
                ) : recipients.map((r, i) => {
                  const tone  = statusTone(r.status);
                  const label = statusLabel(r.status);
                  const icon  = statusIcon(r.status);
                  return (
                    <div key={i} className="px-5 py-4 flex items-start gap-4">
                      <div className="flex flex-col items-center gap-1 pt-0.5 flex-shrink-0">
                        <span className="text-[10px] font-mono text-ink-300 tnum">#{r.signing_order || i + 1}</span>
                        <Avatar name={r.name || r.email} size={36} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-[14px] font-semibold text-ink-900">{r.name || r.email}</div>
                          <Chip tone="ghost">{r.role || 'Signer'}</Chip>
                          <Chip tone={tone} icon={icon}>{label}</Chip>
                        </div>
                        {r.email && r.name && (
                          <div className="text-[12px] text-ink-400 mt-0.5">{r.email}</div>
                        )}
                        {r.signed_at && (
                          <div className="text-[11px] text-ink-400 mt-0.5 tnum">Signed {r.signed_at}</div>
                        )}
                        {r.viewed_at && !r.signed_at && (
                          <div className="text-[11px] text-ink-400 mt-0.5 tnum">Viewed {r.viewed_at}</div>
                        )}
                        {r.ip_address && (
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-ink-400 font-mono tnum">
                            <span className="inline-flex items-center gap-1">
                              <Icon name="dot" size={10} className="text-ink-300" />{r.ip_address}
                            </span>
                            {r.location && (
                              <span className="inline-flex items-center gap-1">
                                <Icon name="dot" size={10} className="text-ink-300" />{r.location}
                              </span>
                            )}
                            {r.device && (
                              <span className="inline-flex items-center gap-1">
                                <Icon name="dot" size={10} className="text-ink-300" />{r.device}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        {r.status === 'pending' && (
                          <button className="h-7 px-2 rounded-md text-[11px] font-medium text-leaf-700 bg-leaf-50 hover:bg-leaf-100 transition-colors">
                            Resend
                          </button>
                        )}
                        <button className="p-1.5 rounded hover:bg-paper-200 text-ink-400 hover:text-ink-700 transition-colors">
                          <Icon name="more" size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right — Documents + Activity ─────────────── */}
            <div className="flex flex-col gap-4 min-h-0">

              {/* Documents in envelope */}
              <div className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 shadow-soft p-5">
                <div className="font-display text-[16px] font-semibold text-ink-900 mb-3">
                  Documents in envelope
                </div>
                <div className="space-y-2">
                  {signingDocs.map((d, i) => {
                    const sigPct = d.status === 'completed' ? 100 : (!d.recipient_count ? 0 : Math.round(((d.signed_count || 0) / d.recipient_count) * 100));
                    return (
                      <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-white ring-1 ring-ink-100">
                        <div className="w-9 h-11 rounded bg-flame-50 flex flex-col items-center justify-center flex-shrink-0">
                          <span className="text-[8px] font-bold text-flame-600 tracking-wider">PDF</span>
                          {d.total_pages && <span className="text-[8px] text-flame-600 tnum">{d.total_pages}p</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] font-semibold text-ink-900 truncate">
                            {d.original_filename || d.title}
                          </div>
                          <div className="text-[11px] text-ink-400 tnum">
                            {d.signed_count || 0}/{d.recipient_count || 0} signatures
                          </div>
                        </div>
                        <div className="w-20 h-1.5 rounded-full bg-ink-100 overflow-hidden flex-shrink-0">
                          <div className="h-full bg-leaf-500 transition-all" style={{ width: `${sigPct}%` }} />
                        </div>
                        <button className="p-1.5 text-ink-400 hover:text-ink-800 flex-shrink-0">
                          <Icon name="eye" size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Activity feed */}
              <div className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 shadow-soft p-5 flex-1 overflow-auto thin-scroll">
                <div className="flex items-center justify-between mb-4">
                  <div className="font-display text-[16px] font-semibold text-ink-900">Activity</div>
                  <button className="text-[11.5px] text-leaf-700 font-medium hover:text-leaf-800">
                    Download audit PDF
                  </button>
                </div>

                {activities.length === 0 ? (
                  <div className="text-center py-6 text-[13px] text-ink-400">No activity yet.</div>
                ) : (
                  <ol className="relative border-l-2 border-ink-100 ml-2 space-y-4 pl-5">
                    {activities.map((ev, i) => {
                      const t = actTone(ev.activity_type || ev.action || '');
                      const dt = ev.created_at ? new Date(ev.created_at) : null;
                      const timeStr = dt
                        ? dt.toLocaleString('en-SG', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '—';
                      return (
                        <li key={i} className="relative">
                          <span className={`absolute -left-[29px] w-5 h-5 rounded-full flex items-center justify-center ring-4 ring-paper-100 ${t.bg} ${t.fg}`}>
                            <Icon name={t.icon} size={9} strokeWidth={2.4} />
                          </span>
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <div className="text-[13px] font-semibold text-ink-900">
                              {ev.description || ev.activity_type || ev.action}
                            </div>
                            <div className="text-[11px] text-ink-400 tnum">{timeStr}</div>
                          </div>
                          {ev.metadata && typeof ev.metadata === 'object' && (
                            <div className="text-[12px] text-ink-500">
                              {ev.metadata.recipient_name || ev.metadata.email || ''}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Download modal */}
      {showDownload && (
        <DownloadFilesModal
          documents={dlDocs}
          envelopeTitle={doc.title}
          onClose={() => setShowDownload(false)}
        />
      )}
    </AppLayout>
  );
};

export default DocumentDetailPage;
